import 'pdfjs-dist/legacy/build/pdf.worker'

import { ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core'
import { MatSnackBar } from '@angular/material/snack-bar'
import { Block } from '@aws-sdk/client-textract'
import { TranslateService } from '@ngx-translate/core'
import * as pdfjsLib from 'pdfjs-dist'
import { PDFPageProxy } from 'pdfjs-dist'
import { BehaviorSubject, from, Observable, Subject, takeUntil } from 'rxjs'

export interface SelectedPagesEventData {
  selectedPages: Set<number>
  totalPages: number
}

@Component({
  selector: 'mmx-pdf-reader',
  templateUrl: './pdf-reader.component.html',
  styleUrls: ['./pdf-reader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PdfReaderComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('pdfCanvas', { read: ElementRef })
  public canvasRef: ElementRef<HTMLCanvasElement> | undefined

  @Input()
  public url!: string

  @Input()
  public page = 1

  @Input()
  public blocks: Block[] = []

  @Input()
  public blocksLoading = false

  @Output()
  public selectionChange = new Subject<SelectedPagesEventData>()

  public pages$!: Observable<{ pageNum: number, pdfPage: PDFPageProxy, thumbnail: string }[]>
  public currentPage = 1
  public totalPages = 0
  public selectedPages$ = new BehaviorSubject<Set<number>>(new Set())

  public loading$ = new BehaviorSubject<boolean>(true)

  private onDestroy$ = new Subject<void>()

  constructor(
    private snackbar: MatSnackBar,
    private translate: TranslateService,
  ) {}

  public ngOnInit() {
    this.selectedPages$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((selectedPages) => this.selectionChange.next({
        selectedPages,
        totalPages: this.totalPages,
      }))

    if (!this.url) {
      return
    }

    this.pages$ = from(this.processPDF())
  }

  public ngOnChanges({ blocksLoading }: SimpleChanges) {
    if (blocksLoading) {
      if (blocksLoading.currentValue) {
        this.snackbar.open(this.translate.instant('pdf-reader.status.ocr-processing'), '', { duration: 5000 })
      } else {
        this.snackbar.dismiss()
      }
    }
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public onPageSelect({ page, event }: any) {
    let shouldLoad = true
    const selectedPages = this.selectedPages$.getValue()
    if (event?.ctrlKey || event?.metaKey) {
      if (selectedPages.has(page.pageNum)) {
        selectedPages.delete(page.pageNum)
        // If we are deselecting a page, we don't want to load it
        shouldLoad = false
      } else {
        selectedPages.add(page.pageNum)
      }
    } else if (event?.shiftKey) {
      const pages = Array.from(selectedPages)
      const start = Math.max(...pages)
      const newPages = Array.from({ length: Math.abs(page.pageNum - start) + 1 }, (_, i) => start + i)
      newPages.forEach((pageNum) => {
        if (!selectedPages.has(pageNum)) {
          selectedPages.add(pageNum)
        }
      })
    } else {
      selectedPages.clear()
      selectedPages.add(page.pageNum)
    }

    this.selectedPages$.next(selectedPages)

    if (shouldLoad) {
      this.loadPage(page)
    }
  }

  public loadPage({ pageNum, pdfPage }: any) {
    if (!this.canvasRef?.nativeElement) {
      return
    }

    this.currentPage = pageNum
    const viewport = pdfPage.getViewport({ scale: 1.5 })
    this.canvasRef.nativeElement.height = viewport.height
    this.canvasRef.nativeElement.width = viewport.width

    pdfPage.render({
      canvasContext: this.canvasRef?.nativeElement.getContext('2d')!,
      viewport,
    })

    this.loading$.next(false)
  }

  private async processPDF() {
    const pdf = await pdfjsLib.getDocument(this.url).promise
    this.totalPages = pdf.numPages
    const pages = []

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const pdfPage = await pdf.getPage(pageNum)
      const thumbnail = await this.getPagePreview(pdfPage)
      pages.push({ pageNum, pdfPage, thumbnail })
    }

    this.onPageSelect({ page: pages[0] })

    return pages
  }

  private async getPagePreview(page: PDFPageProxy): Promise<string> {
    const thumbWidth = 155 * 2
    const viewport = page.getViewport({ scale: 1 })
    const canvas = document.createElement('canvas')
    canvas.width = thumbWidth
    canvas.height = (thumbWidth / viewport.width) * viewport.height
    const scale = Math.min(canvas.width / viewport.width, canvas.height / viewport.height)

    await page.render({
      canvasContext: canvas.getContext('2d')!,
      viewport: page.getViewport({ scale }),
    }).promise

    return canvas.toDataURL()
  }
}
